import {
  BusinessGroupId,
  CMSMetadata,
  MarketPlace,
  PageId,
  PageType,
  Placement,
  Provider,
  Slot,
  TransformationVariant,
  AssetType,
  ProviderLogo,
  LocaleEnum,
  DeviceFamily,
  TabletCategory,
  ContentType,
} from '@amzn/fire-tv-campaign-automation-service';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { PropUtils } from 'src/utils/PropUtils';

import { getMarketingTypeOptions } from '../config/marketingTypeConfiguration';

import { BUSINESS_GROUP_CONFIG, BusinessGroupIdConfig } from './businessGroupConfiguration';
import { PAGE_TYPE_CONFIG, PageTypeConfig } from './pageTypeConfiguration';

/**
 * Maps Page Types to their valid Page IDs per Burton Quip specification
 */
export const PAGE_TYPE_TO_PAGE_IDS: Record<PageType, PageId[]> = {
  [PageType.FTV_BROWSE]: [PageId.HOME_ROT_APPS, PageId.HOME_ROT_FTV, PageId.LOCAL_ROTATOR],
  [PageType.FTV_ROTATOR]: [
    PageId.GAMES,
    PageId.FREE,
    PageId.LIVE,
    PageId.APPS,
    PageId.NEWS,
    PageId.AMBIENT_ART,
    PageId.AMBIENT_PHOTOS,
    PageId.AMBIENT_AI_ART,
    PageId.AMBIENT_MOTION_ART,
    PageId.AMBIENT_INTERACTIVE_ART,
    PageId.SPORTS,
    PageId.AUDIO_AND_MUSIC,
    PageId.SMART_HOME,
    PageId.PROMO,
  ],
  [PageType.BROWSE]: [],
  [PageType.DETAIL]: [],
  [PageType.SEARCH]: [],
  [PageType.MOBILE_APPLICATION_STORE]: [
    PageId.FIRETABLET_FEATUREROTATOR_DRAPER_ALL_PAGE,
    PageId.FIRETABLET_FEATUREROTATOR_DRAPER_GAMES_PAGE,
    PageId.FIRETABLET_FEATUREROTATOR_DRAPER_LISTEN_PAGE,
    PageId.FIRETABLET_FEATUREROTATOR_DRAPER_READ_PAGE,
    PageId.FIRETABLET_FEATUREROTATOR_DRAPER_WATCH_PAGE,
  ],
};

type MetadataItemType = 'text' | 'date-time' | 'drop-down' | 'text-with-support';

interface BaseMetadataConfig {
  type: MetadataItemType;
  label?: string;
  placeholder: string;
  dataTestId: string;
  required?: boolean;
  rowGroup?: string; // Items with the same rowGroup will be rendered in the same row
  width?: 'half' | 'full'; // Default to full if not specified
}

export interface TextMetadataConfig extends BaseMetadataConfig {
  type: 'text';
  minLength?: number;
  maxLength?: number;
  handlerType?: 'onChange' | 'onInput'; // Default to onChange if not specified
}

export interface TextWithSupportMetadataConfig extends BaseMetadataConfig {
  type: 'text-with-support';
  minLength?: number;
  maxLength?: number;
  tooltipConfig?: {
    tips: Array<{ label: string; text: string }>;
  };
  tooltipWidth?: number;
}

export interface DateTimeMetadataConfig extends BaseMetadataConfig {
  type: 'date-time';
}

export interface DropDownMetadataConfig extends BaseMetadataConfig {
  type: 'drop-down';
  options: Array<{ name: string; value: string }>;
}

export type MetadataConfig =
  | TextMetadataConfig
  | TextWithSupportMetadataConfig
  | DateTimeMetadataConfig
  | DropDownMetadataConfig;

/**
 * Maps page IDs to their available slot configurations.
 * Defines which slots are available for each page ID in the campaign form.
 */
const PAGE_ID_TO_SLOT_CONFIGURATIONS: Record<PageId, Slot[]> = {
  // Existing Page IDs
  [PageId.HOME_ROT_FTV]: [
    Slot.GRID_1,
    Slot.GRID_2,
    Slot.GRID_3,
    Slot.GRID_4,
    Slot.GRID_5,
    Slot.GRID_6,
    Slot.GRID_7,
    Slot.GRID_8,
    Slot.GRID_9,
  ],
  [PageId.HOME_ROT_APPS]: [Slot.GRID_1, Slot.GRID_2, Slot.GRID_3, Slot.GRID_4],
  [PageId.LOCAL_ROTATOR]: [
    Slot.FREE_1,
    Slot.FTVC_FREE,
    Slot.FREEVEE,
    Slot.PLUTO,
    Slot.TUBI,
    Slot.LIVE_1,
    Slot.FTVC_LIVE,
    Slot.FREEVEE_LIVE,
    Slot.DIRECTTV,
    Slot.SLING,
    Slot.PHILO,
    Slot.APPS_1,
    Slot.FTVC_APPS,
    Slot.APPS_2,
    Slot.KIDSPLUS,
    Slot.APPS_3,
    Slot.GAMES_1,
    Slot.LUNA,
    Slot.XBOX,
    Slot.FTVC_GAMES,
    Slot.VOLLEY,
    Slot.AUTOMATED_GAMES,
    Slot.AUTOMATED_HOME,
    Slot.AUTOMATED_FREE,
  ],
  // Burton FireTV-Rotator Page IDs per Quip specification
  [PageId.GAMES]: [Slot.EVERGREEN_1, Slot.EVERGREEN_2, Slot.EVERGREEN_3],
  [PageId.FREE]: [Slot.EVERGREEN_1, Slot.EVERGREEN_2, Slot.EVERGREEN_3],
  [PageId.LIVE]: [Slot.EVERGREEN_1, Slot.EVERGREEN_2, Slot.EVERGREEN_3],
  [PageId.APPS]: [Slot.EVERGREEN_1, Slot.EVERGREEN_2, Slot.EVERGREEN_3],
  [PageId.NEWS]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.AUDIO_AND_MUSIC]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.SMART_HOME]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.PROMO]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  // Ambient: 3 slots each
  [PageId.AMBIENT_ART]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.AMBIENT_PHOTOS]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.AMBIENT_AI_ART]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.AMBIENT_MOTION_ART]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  [PageId.AMBIENT_INTERACTIVE_ART]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3],
  // FireTablet pages: 6 slots each
  [PageId.FIRETABLET_FEATUREROTATOR_DRAPER_ALL_PAGE]: [
    Slot.ROSELITE,
    Slot.TURQUOISE,
    Slot.SPINEL,
    Slot.SUNSTONE,
    Slot.MUSTANG,
    Slot.TUNGSTEN,
  ],
  [PageId.FIRETABLET_FEATUREROTATOR_DRAPER_WATCH_PAGE]: [
    Slot.ROSELITE,
    Slot.TURQUOISE,
    Slot.SPINEL,
    Slot.SUNSTONE,
    Slot.MUSTANG,
    Slot.TUNGSTEN,
  ],
  [PageId.FIRETABLET_FEATUREROTATOR_DRAPER_GAMES_PAGE]: [
    Slot.ROSELITE,
    Slot.TURQUOISE,
    Slot.SPINEL,
    Slot.SUNSTONE,
    Slot.MUSTANG,
    Slot.TUNGSTEN,
  ],
  [PageId.FIRETABLET_FEATUREROTATOR_DRAPER_LISTEN_PAGE]: [
    Slot.ROSELITE,
    Slot.TURQUOISE,
    Slot.SPINEL,
    Slot.SUNSTONE,
    Slot.MUSTANG,
    Slot.TUNGSTEN,
  ],
  [PageId.FIRETABLET_FEATUREROTATOR_DRAPER_READ_PAGE]: [
    Slot.ROSELITE,
    Slot.TURQUOISE,
    Slot.SPINEL,
    Slot.SUNSTONE,
    Slot.MUSTANG,
    Slot.TUNGSTEN,
  ],
  [PageId.SPORTS]: [Slot.SLOT_1, Slot.SLOT_2, Slot.SLOT_3, Slot.FTVC_SPORTS, Slot.PV_SPORTS],
};

interface CMSConfigType {
  TRANSFORMATION_VARIANT: Record<AssetType, TransformationVariant[]>;
  PLACEMENTS: Placement[];
  BUSINESS_GROUP_IDS: BusinessGroupIdConfig;
  PAGE_TYPES: PageTypeConfig;
  MARKET_PLACE: MarketPlace[];
  PROVIDER_LOGO: ProviderLogo[];
  LOCALE: Partial<Record<MarketPlace, LocaleEnum[]>>;
}

/**
 * Stage-based CMS configuration map
 * Business logic moved from CDK infrastructure to website layer
 */
const CMS_CONFIG_BY_STAGE: Record<string, CMSConfigType> = {
  [STAGE.DEV]: {
    BUSINESS_GROUP_IDS: BUSINESS_GROUP_CONFIG,
    PAGE_TYPES: PAGE_TYPE_CONFIG,
    MARKET_PLACE: [MarketPlace.US, MarketPlace.CA, MarketPlace.MX],
    PLACEMENTS: [Placement.FEATURE_ROTATOR, Placement.SCREEN_SAVER],
    TRANSFORMATION_VARIANT: {
      [AssetType.BACKGROUND_IMAGE]: [
        TransformationVariant.NO_TRANSFORMATION,
        TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        TransformationVariant.FULL_BLEED,
      ],
      [AssetType.LOGO]: [TransformationVariant.NO_TRANSFORMATION],
      [AssetType.VIDEO_TRAILER]: [TransformationVariant.NO_TRANSFORMATION],
    },
    PROVIDER_LOGO: [
      ProviderLogo.FIRE_TV,
      ProviderLogo.FIRE_CHANNELS,
      ProviderLogo.PRIME_VIDEO,
      ProviderLogo.BET,
      ProviderLogo.AMC_PLUS,
      ProviderLogo.TUBI,
    ],
    LOCALE: {
      [MarketPlace.US]: [LocaleEnum.EN_US, LocaleEnum.ES_US],
      [MarketPlace.CA]: [LocaleEnum.EN_CA, LocaleEnum.FR_CA],
      [MarketPlace.MX]: [LocaleEnum.ES_MX],
    },
  },
  [STAGE.BETA]: {
    BUSINESS_GROUP_IDS: BUSINESS_GROUP_CONFIG,
    PAGE_TYPES: PAGE_TYPE_CONFIG,
    MARKET_PLACE: [MarketPlace.US, MarketPlace.CA, MarketPlace.MX],
    PLACEMENTS: [Placement.FEATURE_ROTATOR, Placement.SCREEN_SAVER],
    TRANSFORMATION_VARIANT: {
      [AssetType.BACKGROUND_IMAGE]: [
        TransformationVariant.NO_TRANSFORMATION,
        TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        TransformationVariant.FULL_BLEED,
      ],
      [AssetType.LOGO]: [TransformationVariant.NO_TRANSFORMATION],
      [AssetType.VIDEO_TRAILER]: [TransformationVariant.NO_TRANSFORMATION],
    },
    PROVIDER_LOGO: [
      ProviderLogo.FIRE_TV,
      ProviderLogo.FIRE_CHANNELS,
      ProviderLogo.PRIME_VIDEO,
      ProviderLogo.BET,
      ProviderLogo.AMC_PLUS,
      ProviderLogo.TUBI,
    ],
    LOCALE: {
      [MarketPlace.US]: [LocaleEnum.EN_US, LocaleEnum.ES_US],
      [MarketPlace.CA]: [LocaleEnum.EN_CA, LocaleEnum.FR_CA],
      [MarketPlace.MX]: [LocaleEnum.ES_MX],
    },
  },
  [STAGE.GAMMA]: {
    BUSINESS_GROUP_IDS: BUSINESS_GROUP_CONFIG,
    PAGE_TYPES: PAGE_TYPE_CONFIG,
    MARKET_PLACE: [
      MarketPlace.US,
      MarketPlace.CA,
      MarketPlace.MX,
      MarketPlace.UK,
      MarketPlace.DE,
      MarketPlace.FR,
      MarketPlace.IT,
      MarketPlace.ES,
      MarketPlace.JP,
      MarketPlace.IN,
      MarketPlace.AU,
    ],
    PLACEMENTS: [Placement.FEATURE_ROTATOR, Placement.SCREEN_SAVER],
    TRANSFORMATION_VARIANT: {
      [AssetType.BACKGROUND_IMAGE]: [
        TransformationVariant.NO_TRANSFORMATION,
        TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        TransformationVariant.FULL_BLEED,
      ],
      [AssetType.LOGO]: [TransformationVariant.NO_TRANSFORMATION],
      [AssetType.VIDEO_TRAILER]: [TransformationVariant.NO_TRANSFORMATION],
    },
    PROVIDER_LOGO: [
      ProviderLogo.FIRE_TV,
      ProviderLogo.FIRE_CHANNELS,
      ProviderLogo.PRIME_VIDEO,
      ProviderLogo.BET,
      ProviderLogo.AMC_PLUS,
      ProviderLogo.TUBI,
    ],
    LOCALE: {
      [MarketPlace.US]: [LocaleEnum.EN_US, LocaleEnum.ES_US],
      [MarketPlace.CA]: [LocaleEnum.EN_CA, LocaleEnum.FR_CA],
      [MarketPlace.MX]: [LocaleEnum.ES_MX],
      [MarketPlace.UK]: [LocaleEnum.EN_GB],
      [MarketPlace.DE]: [LocaleEnum.DE_DE, LocaleEnum.EN_GB],
      [MarketPlace.FR]: [LocaleEnum.FR_FR],
      [MarketPlace.IT]: [LocaleEnum.IT_IT],
      [MarketPlace.ES]: [LocaleEnum.ES_ES],
      [MarketPlace.JP]: [LocaleEnum.JA_JP],
      [MarketPlace.IN]: [LocaleEnum.EN_IN],
      [MarketPlace.AU]: [LocaleEnum.EN_AU],
    },
  },
  [STAGE.PROD_NA]: {
    BUSINESS_GROUP_IDS: BUSINESS_GROUP_CONFIG,
    PAGE_TYPES: PAGE_TYPE_CONFIG,
    MARKET_PLACE: [
      MarketPlace.US,
      MarketPlace.CA,
      MarketPlace.MX,
      MarketPlace.UK,
      MarketPlace.DE,
      MarketPlace.FR,
      MarketPlace.IT,
      MarketPlace.ES,
      MarketPlace.JP,
      MarketPlace.IN,
      MarketPlace.AU,
    ],
    PLACEMENTS: [Placement.FEATURE_ROTATOR],
    TRANSFORMATION_VARIANT: {
      [AssetType.BACKGROUND_IMAGE]: [
        TransformationVariant.NO_TRANSFORMATION,
        TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        TransformationVariant.FULL_BLEED,
      ],
      [AssetType.LOGO]: [TransformationVariant.NO_TRANSFORMATION],
      [AssetType.VIDEO_TRAILER]: [TransformationVariant.NO_TRANSFORMATION],
    },
    PROVIDER_LOGO: [
      ProviderLogo.FIRE_TV,
      ProviderLogo.FIRE_CHANNELS,
      ProviderLogo.PRIME_VIDEO,
      ProviderLogo.BET,
      ProviderLogo.AMC_PLUS,
      ProviderLogo.TUBI,
    ],
    LOCALE: {
      [MarketPlace.US]: [LocaleEnum.EN_US, LocaleEnum.ES_US],
      [MarketPlace.CA]: [LocaleEnum.EN_CA, LocaleEnum.FR_CA],
      [MarketPlace.MX]: [LocaleEnum.ES_MX],
      [MarketPlace.UK]: [LocaleEnum.EN_GB],
      [MarketPlace.DE]: [LocaleEnum.DE_DE, LocaleEnum.EN_GB],
      [MarketPlace.FR]: [LocaleEnum.FR_FR],
      [MarketPlace.IT]: [LocaleEnum.IT_IT],
      [MarketPlace.ES]: [LocaleEnum.ES_ES],
      [MarketPlace.JP]: [LocaleEnum.JA_JP],
      [MarketPlace.IN]: [LocaleEnum.EN_IN],
      [MarketPlace.AU]: [LocaleEnum.EN_AU],
    },
  },
  [STAGE.PROD_FE]: {
    BUSINESS_GROUP_IDS: BUSINESS_GROUP_CONFIG,
    PAGE_TYPES: PAGE_TYPE_CONFIG,
    MARKET_PLACE: [MarketPlace.US, MarketPlace.CA, MarketPlace.MX],
    PLACEMENTS: [Placement.FEATURE_ROTATOR],
    TRANSFORMATION_VARIANT: {
      [AssetType.BACKGROUND_IMAGE]: [
        TransformationVariant.NO_TRANSFORMATION,
        TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY,
        TransformationVariant.FULL_BLEED,
      ],
      [AssetType.LOGO]: [TransformationVariant.NO_TRANSFORMATION],
      [AssetType.VIDEO_TRAILER]: [TransformationVariant.NO_TRANSFORMATION],
    },
    PROVIDER_LOGO: [
      ProviderLogo.FIRE_TV,
      ProviderLogo.FIRE_CHANNELS,
      ProviderLogo.PRIME_VIDEO,
      ProviderLogo.BET,
      ProviderLogo.AMC_PLUS,
      ProviderLogo.TUBI,
    ],
    LOCALE: {
      [MarketPlace.US]: [LocaleEnum.EN_US, LocaleEnum.ES_US],
      [MarketPlace.CA]: [LocaleEnum.EN_CA, LocaleEnum.FR_CA],
      [MarketPlace.MX]: [LocaleEnum.ES_MX],
      [MarketPlace.JP]: [LocaleEnum.JA_JP],
    },
  },
};

/**
 * Comprises the stage specific CMS Configuration
 * Tests automatically use Dev configuration
 */
export const CMSConfig: CMSConfigType = (() => {
  const stage = process.env.NODE_ENV === 'test' ? STAGE.DEV : ProvisionedConstants.STAGE;
  return CMS_CONFIG_BY_STAGE[stage] || CMS_CONFIG_BY_STAGE[STAGE.DEV];
})();

export const BASE_CAMPAIGN_METADATA_CONFIG: Partial<Record<keyof CMSMetadata, MetadataConfig>> = {
  title: {
    type: 'text',
    label: 'Title',
    placeholder: 'Enter campaign title',
    dataTestId: 'title-input',
    required: true,
    rowGroup: 'row1',
    minLength: 1,
    maxLength: 150,
  },
  businessGroupId: {
    type: 'drop-down',
    label: 'Business Group',
    placeholder: 'Select business group',
    dataTestId: 'business-group-dropdown',
    rowGroup: 'row2',
    width: 'half',
    options: [],
  },

  pageType: {
    type: 'drop-down',
    label: 'Page Type',
    placeholder: 'Select page type',
    dataTestId: 'page-type-dropdown',
    rowGroup: 'row3',
    options: [],
  },
  pageId: {
    type: 'drop-down',
    label: 'Page ID',
    placeholder: 'Select page ID',
    dataTestId: 'page-id-dropdown',
    rowGroup: 'row4',
    width: 'half',
    options: [],
  },
  slotName: {
    type: 'drop-down',
    label: 'Slot',
    placeholder: 'Select slot',
    dataTestId: 'slot-dropdown',
    rowGroup: 'row4',
    width: 'half',
    options: PropUtils.createOptionsFromEnum(Slot),
  },
  campaignStartDate: {
    type: 'date-time',
    placeholder: 'Select start date and time',
    dataTestId: 'campaign-start-date-input',
    required: true,
  },
  campaignEndDate: {
    type: 'date-time',
    placeholder: 'Select end date and time',
    dataTestId: 'campaign-end-date-input',
    required: true,
  },
  clickThrough: {
    type: 'text-with-support',
    label: 'Click Through URL',
    placeholder: 'ASIN / URL',
    dataTestId: 'click-through-input',
    required: true,
    rowGroup: 'row7',
    minLength: 10,
    maxLength: 2048,
    tooltipWidth: 370,
    tooltipConfig: {
      tips: [
        {
          label: 'ASIN',
          text: 'B09KDWJM8B',
        },
        {
          label: 'URL',
          text: 'content://com.amazon.mediabrowse/detail?provider=aiv&providerId=B09KDWJM8B',
        },
      ],
    },
  },
  accessibilityText: {
    type: 'text',
    label: 'Accessibility Text',
    placeholder: 'Enter accessibility text',
    dataTestId: 'accessibility-text-input',
    rowGroup: 'row8',
    minLength: 1,
    maxLength: 150,
    required: true,
  },
};

/**
 * Device-family-specific metadata fields configuration
 * Defines additional fields that should be rendered for specific device families
 */
export const DEVICE_FAMILY_SPECIFIC_METADATA_CONFIG: Partial<
  Record<string, Partial<Record<keyof CMSMetadata, MetadataConfig>>>
> = {
  [DeviceFamily.FIRE_TV]: {
    provider: {
      type: 'drop-down',
      label: 'Provider',
      placeholder: 'Select provider',
      dataTestId: 'provider-dropdown',
      rowGroup: 'row2',
      width: 'half',
      options: PropUtils.createOptionsFromEnum(Provider),
      required: true,
    },
  },
  [DeviceFamily.FIRE_TABLETS]: {
    featureRotatorText: {
      type: 'text',
      label: 'Body Copy',
      placeholder: 'Enter body copy (max 40 characters)',
      dataTestId: 'body-copy-input',
      rowGroup: 'row9',
      maxLength: 40,
      required: true,
      handlerType: 'onInput',
    },
    category: {
      type: 'drop-down',
      label: 'Category',
      placeholder: 'Select Category',
      dataTestId: 'category-dropdown',
      rowGroup: 'row10',
      required: true,
      options: PropUtils.createOptionsFromEnum(TabletCategory),
    },
    contentType: {
      type: 'drop-down',
      label: 'Content Type',
      placeholder: 'Select Content Type',
      dataTestId: 'content-type-dropdown',
      rowGroup: 'row10',
      required: true,
      options: PropUtils.createOptionsFromEnum(ContentType),
    },
    marketingType: {
      type: 'drop-down',
      label: 'Marketing Type',
      placeholder: 'Select Marketing Type',
      dataTestId: 'marketing-type-dropdown',
      rowGroup: 'row11',
      required: true,
      options: getMarketingTypeOptions(),
    },
  },
};

/**
 * Retrieves slot options for a given page ID formatted for dropdown selection.
 * These options are used to populate the slot dropdown in the campaign metadata form.
 *
 * @param pageId - The page ID to get slot options for
 * @returns Array of slot options formatted as dropdown options with name and value properties
 */
export function getSlotOptionsForPageId(pageId: string): { name: string; value: string }[] {
  // Validate if pageId is a valid PageId enum value
  if (!Object.values(PageId).includes(pageId as PageId)) {
    return [];
  }

  // Access with fallback to empty array if configuration doesn't exist
  const slotConfig = PAGE_ID_TO_SLOT_CONFIGURATIONS[pageId as PageId] || [];
  return PropUtils.createOptionsFromEnumValues(slotConfig);
}

/**
 * Get valid Page IDs for a given Page Type
 */
export function getPageIdsForPageType(pageType: PageType): { name: string; value: string }[] {
  const pageIds = PAGE_TYPE_TO_PAGE_IDS[pageType] || [];
  return PropUtils.createOptionsFromEnumValues(pageIds);
}

/*
 * Get BusinessGroup options for a given DeviceFamily and Placement
 */
export function getBusinessGroupOptions(
  deviceFamily: DeviceFamily,
  placement: Placement,
): { name: string; value: string }[] {
  const businessGroupIds = CMSConfig.BUSINESS_GROUP_IDS?.[deviceFamily]?.[placement] || [];
  return PropUtils.getOptionsWithUnderscoresRemoved(
    PropUtils.createOptionsFromEnumValues(businessGroupIds, BusinessGroupId),
  );
}

/*
 * Get Page Type options for a given DeviceFamily and Placement
 */
export function getPageTypeOptions(
  deviceFamily: DeviceFamily,
  placement: Placement,
): { name: string; value: string }[] {
  const pageTypes = CMSConfig.PAGE_TYPES?.[deviceFamily]?.[placement] || [];
  return PropUtils.createOptionsFromEnumValues(pageTypes);
}

/**
 * Gets the complete metadata configuration including device-family-specific fields
 *
 * @param deviceFamily - Device family to include specific fields
 * @returns Merged metadata configuration
 */
export function getMetadataConfig(
  deviceFamily: DeviceFamily,
): Partial<Record<keyof CMSMetadata, MetadataConfig>> {
  const deviceFamilyFields = DEVICE_FAMILY_SPECIFIC_METADATA_CONFIG[deviceFamily] || {};
  return { ...BASE_CAMPAIGN_METADATA_CONFIG, ...deviceFamilyFields };
}

export { getTargetSegmentConfiguration } from './targetSegmentConfiguration';
